import type {
  ControlSettingStateType,
  EControlSettingType,
  IControlSettingData,
  IDynamicControlSetting,
  PayloadControlChange,
} from '../../type';

import { useControlDataChange, useResetControlData } from '../hooks';
import { useControlSettingStore } from '../../ReportFilter/stores';
import { BlockStack, Box, Scrollable } from '@shopify/polaris';
import { useControlSetting } from '../../ReportFilter/hooks';
import { Control } from './Control';
import { useEffect } from 'react';

interface ControlSettingsProps {
  data: IControlSettingData;
  defaultData: IControlSettingData;
  presetUI?: IDynamicControlSetting;
  state?: ControlSettingStateType;
  type: EControlSettingType;
  controlChange: (data: PayloadControlChange) => void;
  maxHeight?: number;
  onDeleteControlUI?: (id: string) => void;
}

export const ControlSettings: React.FC<ControlSettingsProps> = ({
  data = {},
  state,
  presetUI,
  type,
  maxHeight,
  defaultData,
  controlChange,
  onDeleteControlUI,
}) => {
  const updateSettingUI = useControlSettingStore((state) => state.updateSettingUIByPreset);
  const deleteControlUI = useControlSettingStore((state) => state.deleteControlUI);
  const { handleResetControlData } = useResetControlData({ type, defaultData, controlChange });

  const handleDeleteControlUI = (id: string) => {
    deleteControlUI(id, type);
    onDeleteControlUI?.(id);
  };

  useControlDataChange({
    type,
    controlChange,
    controlUIChange: updateSettingUI,
    deleteControlUI: handleDeleteControlUI,
    resetControlData: handleResetControlData,
  });

  const mapControlData = useControlSetting({ type, controlData: data });

  useEffect(() => {
    if (!presetUI) return;

    updateSettingUI(presetUI, type);
  }, [JSON.stringify(presetUI), type]); //eslint-disable-line

  if (!mapControlData.length) return null;

  return (
    <Scrollable
      className={`p-3`}
      scrollbarWidth="thin"
      style={{ maxHeight: maxHeight ? `${maxHeight}px` : 'calc(100vh-24px)' }}
    >
      <BlockStack gap="400">
        {mapControlData.map((setting) => {
          const mapSetting = {
            ...setting,
            ...state?.[setting.id],
            connector: setting.connector
              ? {
                  ...setting.connector,
                  value: data[setting.connector.key].value,
                }
              : undefined,
          };

          if (mapSetting.hide) return null;
          return (
            <Box key={mapSetting.id} borderRadius="200" borderColor="border" borderWidth="025">
              <Control data={mapSetting} key={mapSetting.id} type={type} />
            </Box>
          );
        })}
      </BlockStack>
    </Scrollable>
  );
};
